<script setup>
/**
 * 复刻 Ionic 4 的 <ion-alert>（md 模式），支持两种形态：
 *   1. radio 单选（inputs 非空）—— ion-select 点开后弹的就是这个
 *   2. 纯消息确认（inputs 为空）—— 旧版 alertCtrl.create({header,message,buttons}) 用的形态
 *
 * 逆向来源：web/149.792875766ce881fa6086.js（styleMode = "md"）
 *   DOM 结构取自 render() / renderRadio() / renderAlertButtons()
 *   CSS 逐条取自该文件的 style getter
 *
 * 旧版 ion-select 打开的 alert：
 *   header 取关联 ion-label 的文本（牌九下注记录里是「结算状态:」）
 *   buttons = [{text: cancelText, role:'cancel'}, {text: okText, handler}]
 *   md 进场动画：backdrop opacity .01→.32，wrapper opacity .01→1 + scale .9→1，150ms ease-in-out
 *
 * ion-alert 的 render()（从 web/149 里抽出来的原文）：
 *   [ <ion-backdrop tappable={backdropDismiss}/>,
 *     <div class="alert-wrapper">
 *       <div class="alert-head">
 *         {header    && <h2 class="alert-title">{header}</h2>}
 *         {subHeader && <h2 class="alert-sub-title">{subHeader}</h2>}
 *       </div>
 *       <div class="alert-message" innerHTML={message}/>   ← message 是 innerHTML，不是文本
 *       {renderAlertInputs()}
 *       {renderAlertButtons()} ]
 *   renderAlertButtons()：
 *     <div class={{'alert-button-group':true,'alert-button-group-vertical':buttons.length>2}}>
 *     所以 1~2 个按钮横排，超过 2 个才竖排。
 *
 * 单按钮形态：appService.showAlert 的签名（从 main.js 里抽出来的）是
 *   showAlert(header, message, subHeader = "", buttons = ["确定"])
 *     → alertCtrl.create({ header, subHeader, message, buttons })
 * 首页的弹窗公告走的就是这个，只有一个「确定」按钮。
 * 传 :buttons="['确定']" 即可得到这种形态；不传 buttons 时保持原来的
 * 「取消 / 确定」两按钮，兼容已有调用方。
 */
import { computed, ref, watch } from 'vue'

const props = defineProps({
  header: { type: String, default: '' },
  message: { type: String, default: '' },
  // radio 选项：[{ value, label }]
  inputs: { type: Array, default: () => [] },
  wrapLabels: { type: Boolean, default: false },
  // 当前选中值（对应旧版 processedInputs 里 checked 的那一项）
  value: { type: [String, Number], default: undefined },
  cancelText: { type: String, default: '取消' },
  okText: { type: String, default: '确定' },
  /**
   * 对齐 ion-alert 的 buttons：可以是字符串，也可以是 {text, role}。
   * 不传（null）时沿用「取消 / 确定」两按钮，兼容已有调用方。
   */
  buttons: { type: Array, default: null },
})
const emit = defineEmits(['cancel', 'confirm'])

/** 旧版 buttonsChanged → processedButtons：字符串会被规整成 {text} */
const processedButtons = computed(() => (props.buttons || []).map((button) => (
  typeof button === 'string' ? { text: button, role: undefined } : { text: button.text, role: button.role }
)))

// 旧版 rbClick 是就地改 processedInputs 的 checked，这里用本地选中值
const selected = ref(props.value)
watch(() => props.value, (next) => { selected.value = next })

/** 旧版 rbClick(input) */
function rbClick(input) { selected.value = input.value }

/**
 * 旧版 buttonClick：role==='cancel' 直接 dismiss；否则跑 handler 再 dismiss。
 * ion-select 的 okText 按钮 handler 会把选中值写回 ngModel。
 */
function onCancel() { emit('cancel') }
function onOk() { emit('confirm', selected.value) }

/** buttons 模式下的点击：role==='cancel' 当取消，其余当确认 */
function onButton(button) {
  if (button.role === 'cancel') { emit('cancel'); return }
  emit('confirm', selected.value)
}
</script>

<template>
  <!-- 旧版 hostData：role=dialog、aria-modal、z-index 20000+overlayIndex -->
  <div class="legacy-alert" :class="{ 'wrap-labels': wrapLabels }" role="dialog" aria-modal="true">
    <!-- 旧版 <ion-backdrop tappable>，md backdrop 是 #000 / opacity .32 -->
    <div class="alert-backdrop" @click="onCancel"></div>

    <div class="alert-wrapper">
      <div class="alert-head">
        <h2 v-if="header" class="alert-title">{{ header }}</h2>
      </div>
      <!-- 旧版这个 div 始终存在，:empty 时 padding 归 0。
           注意 ion-alert 用的是 innerHTML={message}，所以这里也用 v-html：
           首页的弹窗公告 message 就是后台填的富文本（跟旧版一致的信任边界）。 -->
      <div class="alert-message" v-html="message"></div>

      <!-- 旧版 renderRadio() -->
      <div v-if="inputs.length" class="alert-radio-group" role="radiogroup">
        <button
          v-for="input in inputs"
          :key="input.value"
          type="button"
          class="alert-radio-button alert-tappable alert-radio"
          role="radio"
          :aria-checked="String(input.value === selected)"
          :tabindex="0"
          @click="rbClick(input)"
        >
          <div class="alert-button-inner">
            <div class="alert-radio-icon"><div class="alert-radio-inner"></div></div>
            <div class="alert-radio-label">{{ input.label }}</div>
          </div>
        </button>
      </div>

      <!-- 旧版 renderAlertButtons()：两个以内横排，超过两个加 .alert-button-group-vertical -->
      <div class="alert-button-group" :class="{ 'alert-button-group-vertical': processedButtons.length > 2 }">
        <!-- 传了 buttons 就照 buttons 渲染（首页弹窗公告是 ['确定'] 单按钮） -->
        <template v-if="buttons">
          <button
            v-for="(button, index) in processedButtons"
            :key="index"
            type="button"
            class="alert-button"
            :tabindex="0"
            @click="onButton(button)"
          >
            <span class="alert-button-inner">{{ button.text }}</span>
          </button>
        </template>
        <!-- 默认形态：取消 / 确定 -->
        <template v-else>
          <button type="button" class="alert-button" :tabindex="0" @click="onCancel">
            <span class="alert-button-inner">{{ cancelText }}</span>
          </button>
          <button type="button" class="alert-button" :tabindex="0" @click="onOk">
            <span class="alert-button-inner">{{ okText }}</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* --- 以下逐条照抄 web/149 的 md 样式，.sc-ion-alert-md-h → .legacy-alert --- */
.legacy-alert{
  --min-width:250px;--width:auto;--min-height:auto;--height:auto;--max-height:90%;
  --max-width:280px;--background:#fff;
  left:0;right:0;top:0;bottom:0;display:flex;position:fixed;align-items:center;justify-content:center;
  contain:strict;touch-action:none;user-select:none;font-size:14px;
  /* 旧版行内 style 是 zIndex: 20000 + overlayIndex */
  z-index:20000;
}
/* ion-backdrop md：黑底 .32 透明度（进场动画 .01 → .32） */
.alert-backdrop{position:absolute;left:0;top:0;width:100%;height:100%;background:#000;opacity:.32;animation:alert-backdrop-in 150ms ease-in-out}

.alert-wrapper{
  display:flex;flex-direction:column;
  width:var(--width);min-width:var(--min-width);max-width:var(--max-width);
  height:var(--height);min-height:var(--min-height);max-height:var(--max-height);
  background:var(--background);contain:content;z-index:10;
  border-radius:4px;
  box-shadow:0 11px 15px -7px rgba(0,0,0,.2),0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12);
  /* md 进场：opacity .01→1 + scale .9→1，150ms ease-in-out */
  animation:alert-wrapper-in 150ms ease-in-out;
}
@keyframes alert-backdrop-in{from{opacity:.01}to{opacity:.32}}
@keyframes alert-wrapper-in{from{opacity:.01;transform:scale(.9)}to{opacity:1;transform:scale(1)}}

.alert-head{padding:20px 23px 15px;text-align:start}
.alert-title{margin:0;padding:0;font-size:20px;font-weight:500;color:#000}

.alert-message{
  box-sizing:border-box;-webkit-overflow-scrolling:touch;overflow-y:scroll;overscroll-behavior-y:contain;
  padding:20px 24px;color:#737373;max-height:240px;font-size:16px;
}
.alert-message::-webkit-scrollbar,.alert-radio-group::-webkit-scrollbar{display:none}
.alert-message:empty{padding:0}
/* message 走 innerHTML，富文本里的图片不要撑破弹窗（旧版靠全局 reset，这里补上） */
.alert-message :deep(img){max-width:100%;height:auto}
/* 旧版 .alert-head + .alert-message{padding-top:0} */
.alert-head + .alert-message{padding-top:0}

.alert-radio-group{position:relative;max-height:240px;border-top:1px solid #d9d9d9;border-bottom:1px solid #d9d9d9;overflow:auto}

.alert-tappable{
  margin:0;padding:0;border:0;background:transparent;font-size:inherit;line-height:normal;text-align:start;
  appearance:none;contain:strict;position:relative;height:48px;overflow:hidden;display:flex;width:100%;outline:none;
}
.alert-radio .alert-button-inner{display:flex;width:100%;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;height:100%}

.alert-radio-icon{box-sizing:border-box;left:26px;top:0;border-radius:50%;display:block;position:relative;width:20px;height:20px;border-width:2px;border-style:solid;border-color:#737373}
.alert-radio-inner{
  box-sizing:border-box;left:3px;top:3px;border-radius:50%;position:absolute;width:10px;height:10px;
  transform:scale3d(0,0,0);transition:transform .28s cubic-bezier(.4,0,.2,1);
  background-color:var(--legacy-theme,#3880ff);
}
.alert-radio-label{padding:13px 26px 13px 52px;flex:1;color:#262626;font-size:16px;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}
.wrap-labels .alert-radio{contain:content;height:auto;min-height:48px}
.wrap-labels .alert-radio-label{white-space:normal;overflow-wrap:anywhere;line-height:1.4}
[aria-checked=true] .alert-radio-label{color:#262626}
[aria-checked=true] .alert-radio-icon{border-color:var(--legacy-theme,#3880ff)}
[aria-checked=true] .alert-radio-inner{transform:scaleX(1)}

.alert-button-group{
  display:flex;flex-direction:row;width:100%;
  padding:8px;box-sizing:border-box;flex-wrap:wrap-reverse;justify-content:flex-end;
}
/* 旧版 renderAlertButtons() 在按钮多于 2 个时加这个类 */
.alert-button-group-vertical{flex-direction:column;flex-wrap:nowrap}
.alert-button{
  border-radius:2px;margin:0 8px 0 0;padding:10px;position:relative;
  background-color:transparent;color:var(--legacy-theme,#3880ff);
  font-weight:500;text-align:end;text-transform:uppercase;overflow:hidden;
  display:block;border:0;font-size:14px;line-height:20px;z-index:0;outline:none;cursor:pointer;
}
.alert-button .alert-button-inner{display:flex;width:100%;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:flex-end;height:100%}
</style>
